import { toast } from 'sonner';

const API_BASE_URL = import.meta.env.VITE_URL_API?.replace(/\/$/, '') || 'http://localhost:3230';

let accessToken: string | null = null;
let isRefreshing = false;
let refreshAllowed = true;
let refreshQueue: Array<(token: string | null) => void> = [];

// ─── Token management ───────────────────────────────────
export function setAccessToken(token: string | null) {
  accessToken = token;
  // This is a client safety boundary only; authorization remains in the API.
  // Never exchange the shared admin cookie while using an impersonation token.
  refreshAllowed = true;
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) as {
        tokenUse?: string;
        impersonatedBy?: number;
      };
      refreshAllowed = payload.tokenUse !== 'impersonation' && payload.impersonatedBy === undefined;
    } catch {
      /* Invalid tokens are rejected by the API. */
    }
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

// ─── Response unwrapper ─────────────────────────────────
// L'API enveloppe toutes les réponses dans { success: true, data: T }.
// Les réponses paginées ont aussi un champ `meta` : { success, data: T[], meta }.
// → on retourne { data, meta } pour les paginées, data seul pour les simples.
function unwrapJson<T>(json: unknown): T {
  const obj = json as { success?: boolean; data?: unknown; meta?: unknown };
  if (obj.success === true && obj.data !== undefined) {
    if (obj.meta !== undefined) {
      return { data: obj.data, meta: obj.meta } as T;
    }
    return obj.data as T;
  }
  return json as T;
}

// ─── Core fetch wrapper ─────────────────────────────────
async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  if (!refreshAllowed && endpoint === '/auth/refresh') throw new Error('Impersonation session cannot be refreshed');
  const requestToken = accessToken;
  const url = `${API_BASE_URL}/v1${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (options.body instanceof FormData) delete headers['Content-Type'];

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // Pour envoyer les cookies (refreshToken)
  });

  // ─── 401 → tentative de refresh ──────────────────────
  if (response.status === 401 && accessToken && refreshAllowed && requestToken === accessToken) {
    const newToken = await handleTokenRefresh();
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`;
      const retryResponse = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });

      if (!retryResponse.ok) {
        throw await buildError(retryResponse, !endpoint.startsWith('/site-cms/'));
      }
      return unwrapJson<T>(await retryResponse.json());
    }
  }

  if (!response.ok) {
    throw await buildError(response, !endpoint.startsWith('/site-cms/'));
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return unwrapJson<T>(await response.json());
}

// ─── Refresh logic (avec queue) ─────────────────────────
async function handleTokenRefresh(): Promise<string | null> {
  if (isRefreshing) {
    // Si un refresh est déjà en cours, attendre son résultat
    return new Promise<string | null>(resolve => {
      refreshQueue.push(resolve);
    });
  }

  const refreshingToken = accessToken;
  isRefreshing = true;

  try {
    const response = await fetch(`${API_BASE_URL}/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // Cookie refreshToken envoyé automatiquement
    });

    if (!response.ok) {
      if (accessToken !== refreshingToken || !refreshAllowed) return null;
      // Refresh failed for the current identity.
      setAccessToken(null);
      toast.info('Session expirée, veuillez vous reconnecter');
      window.dispatchEvent(new CustomEvent('auth:logout'));
      return null;
    }

    const data = unwrapJson<{ accessToken: string }>(await response.json());
    const newToken = data.accessToken;
    if (accessToken !== refreshingToken || !refreshAllowed) return null;
    setAccessToken(newToken);

    // Résoudre toutes les requêtes en attente
    refreshQueue.forEach(resolve => resolve(newToken));
    refreshQueue = [];

    return newToken;
  } catch {
    if (accessToken !== refreshingToken || !refreshAllowed) return null;
    setAccessToken(null);
    window.dispatchEvent(new CustomEvent('auth:logout'));
    return null;
  } finally {
    refreshQueue.forEach(resolve => resolve(null));
    refreshQueue = [];
    isRefreshing = false;
  }
}

/** Lets callers keep fallback notifications without repeating an HTTP error toast. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly toastShown: boolean,
    readonly status?: number,
    readonly code?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ─── Error builder ──────────────────────────────────────
async function buildError(response: Response, notify = true): Promise<Error> {
  let body: { message?: unknown; code?: unknown; details?: unknown } = {};
  try {
    const parsed: unknown = await response.json();
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) body = parsed as typeof body;
  } catch {
    /* Non-JSON failures retain their HTTP status. */
  }
  const message =
    typeof body.message === 'string'
      ? body.message
      : Array.isArray(body.message)
        ? body.message.filter(value => typeof value === 'string').join(' · ')
        : `Erreur ${response.status}`;
  const showToast = notify && response.status !== 401;
  if (showToast) toast.error(message);
  return new ApiError(
    message,
    showToast,
    response.status,
    typeof body.code === 'string' ? body.code : undefined,
    body.details,
  );
}

// Binary downloads use the same token-refresh policy as JSON requests.
async function requestBlob(endpoint: string): Promise<Blob> {
  const url = `${API_BASE_URL}/v1${endpoint}`;
  const originalToken = accessToken;
  async function send(token: string | null): Promise<Response> {
    return fetch(url, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });
  }
  let response = await send(originalToken);
  if (response.status === 401 && originalToken && refreshAllowed && originalToken === accessToken) {
    const refreshed = await handleTokenRefresh();
    if (refreshed) response = await send(refreshed);
  }
  if (!response.ok) throw await buildError(response, !endpoint.startsWith('/site-cms/'));
  return response.blob();
}

// ─── Public API ─────────────────────────────────────────
export const api = {
  get: <T>(endpoint: string) => request<T>(endpoint, { method: 'GET' }),

  getBlob: (endpoint: string) => requestBlob(endpoint),

  postMultipart: <T>(endpoint: string, body: FormData) => request<T>(endpoint, { method: 'POST', body }),

  post: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(endpoint: string) => request<T>(endpoint, { method: 'DELETE' }),
};
